import { supabase } from "./supabase";
import { fixtureKickoffUtc } from "@nrl/shared";
import { appNow, getAppClock } from "./appClock";

// ─────────────────────────────────────────────────────────────────────────────
// Admin weekly confirmation API (Phase 4, round 6 wave B).
//
// Confirmations reference the player ROW (match_confirmation_players keyed by
// player_id), not typed names. Same-name players are told apart by date of
// birth. The client writes ONLY through server RPCs (save_match_confirmation,
// admin_add_player, admin_set_player_identity, acknowledge_admin_notification);
// it never writes match_confirmations or its children directly (SEC-3/SEC-4).
// The release-round Edge Function flips a round's pending xp_ledger rows to
// 'released'.
//
// Error mapping mirrors checkinApi.js / packApi.js: a FunctionsHttpError carries
// a `.context` (the fetch Response) whose JSON body's `error` field names the
// machine code; an unknown code degrades to `server_error`; a thrown fetch is
// `network_error`. RPC (PostgREST) errors carry `.code` (42501 → forbidden) and
// a `.message` whose stable token prefix names the failure.
// ─────────────────────────────────────────────────────────────────────────────

const PREFILL_CODES = new Set([
  "no_external_ref",
  "feed_not_ready",
  "forbidden",
  "unauthenticated",
  "fixture_not_found",
  "invalid_input",
  "feed_unavailable",
  "budget_exhausted",
  "server_error",
  // R14: orientation_mismatch is allowlisted so the draft is kept and the
  // editor offers manual entry instead of degrading to server_error.
  "orientation_mismatch",
]);

const RELEASE_CODES = new Set([
  "matches_unconfirmed",
  "round_not_finished",
  "forbidden",
  "unauthenticated",
  "invalid_input",
  "round_not_found",
  "budget_exhausted",
  "server_error",
  // R5: an incomplete Origin line-up refuses the release with 422.
  "origin_lineup_incomplete",
]);

const SYNC_CODES = new Set([
  "unknown_alias",
  "no_data",
  "provider_mismatch",
  "feed_unavailable",
  "budget_exhausted",
  "forbidden",
  "unauthenticated",
  "invalid_input",
  "write_failed",
  "server_error",
]);

// Stable error tokens raised by the save/add/stamp RPCs (see migration
// 20260917120000). A PostgREST error message begins with one of these tokens.
const CONFIRMATION_TOKENS = [
  "player_not_in_team",
  "unknown_player",
  "duplicate_player",
  "invalid_count",
  "invalid_side",
  "unknown_milestone",
  "milestone_player_not_in_lineup",
  "namesake_requires_date_of_birth",
  "namesake_missing_date_of_birth",
  "external_ref_taken",
  "invalid_source",
  "invalid_name",
  "unknown_team",
  "unknown_notification",
  // Round 10 — squad-membership RPC tokens (admin_add_to_squad,
  // admin_move_player, admin_end_membership, admin_roll_squads).
  "membership_overlap",
  "same_team",
  "no_membership",
  "invalid_date",
  "invalid_kind",
  "invalid_reason",
  "unknown_membership",
  "invalid_seasons",
  // Round 11 — admin_app_clock / admin_set_location_standin tokens (R7/R8/R14).
  "replay_off",
  "no_next_fixture",
  "invalid_at",
  "invalid_action",
  "clock_backwards",
  "unknown_member",
  // Round 15 — Origin line-up validation tokens (R4.2/R4.3/R4.4/R16).
  // `origin_lineup_size` carries the PostgREST DETAIL `{"home":n,"away":n}` on
  // `.details`; rpcErrorResult preserves it so the editor can recover the
  // per-side counts.
  "origin_lineup_size",
  "origin_no_milestones",
  "bad_fixture_sides",
];

async function errorResult(error, knownCodes) {
  if (error?.context && typeof error.context.json === "function") {
    try {
      const body = await error.context.json();
      const code = knownCodes.has(body?.error) ? body.error : "server_error";
      return {
        ok: false, code,
        ...(body?.fixtures ? { fixtures: body.fixtures } : {}),
        // R5a: forward the finish-gate payload so the UI can name the release
        // time in the final fixture's venue timezone.
        ...(body?.releasable_at ? { releasable_at: body.releasable_at } : {}),
        ...(body?.final_fixture_id ? { final_fixture_id: body.final_fixture_id } : {}),
      };
    } catch {
      return { ok: false, code: "server_error" };
    }
  }
  return { ok: false, code: "network_error" };
}

// Map a PostgREST RPC error to a stable token: 42501 → forbidden; a message
// starting with a known token → that token; otherwise server_error. Exported
// for reuse by squadsApi.js (same RPC error-mapping pattern, round 10).
// R16: the PostgREST `.details` field is preserved on a token match so the
// editor can recover the per-side counts from `origin_lineup_size`'s DETAIL
// `{"home":n,"away":n}`.
export function rpcErrorResult(error) {
  if (!error) return { ok: false, code: "server_error" };
  if (error.code === "42501") return { ok: false, code: "forbidden" };
  const msg = String(error.message || "");
  for (const token of CONFIRMATION_TOKENS) {
    if (msg.startsWith(token)) {
      return { ok: false, code: token, ...(error.details != null ? { details: error.details } : {}) };
    }
  }
  return { ok: false, code: "server_error" };
}

/**
 * Fetch the admin confirmation queue: every fixture of the current app-clock
 * season whose kickoff INSTANT (the venue-aware kickoffUtc, not the venue's
 * calendar date against the viewer's date) is at or before `now`, with its
 * embedded venue, its team ids, and any match_confirmation row. Also reads
 * round_confirmations for the server-side released state (SEC-3/SEC-4).
 * Returns an array, or null on error.
 *
 * R16 v2: under a staging replay the viewer's wall-clock date is meaningless
 * (a Vegas fixture's venue-local date can sit a day either side of its app
 * time), so the queue follows the ONE app clock (`now = appNow()`) and the
 * kickoff instant, scoped to `current_season` so a replay never surfaces a
 * future season's fixtures early or hides this season's late games.
 */
export async function fetchConfirmationQueue({ now = appNow() } = {}) {
  const currentSeason = getAppClock()?.current_season ?? now.getUTCFullYear();
  try {
    const fixturesRes = await supabase
      .from("fixtures")
      .select(
        "id, round, scoring_round, is_origin, origin_game, match_date, kickoff_local, home_ref, away_ref, external_ref, status, home_team_id, away_team_id, venue:venues(name,timezone), confirmation:match_confirmations(id, source, confirmed_at, players:match_confirmation_players(player_id,side))",
      )
      .order("round", { ascending: false });

    if (fixturesRes.error) {
      console.warn("[adminApi] could not load confirmation queue:", fixturesRes.error.message);
      return null;
    }

    let roundsRes = { data: null, error: null };
    try {
      roundsRes = await supabase
        .from("round_confirmations")
        .select("season_year, round, status, released_at")
        .order("round", { ascending: false });
    } catch (roundsErr) {
      console.warn("[adminApi] could not load round_confirmations:", roundsErr?.message || roundsErr);
    }
    const releaseByRound = new Map();
    const roundRows = Array.isArray(roundsRes?.data) ? roundsRes.data : [];
    for (const rr of roundRows) {
      if (rr?.released_at) {
        releaseByRound.set(`${rr.season_year}:${rr.round}`, rr.released_at);
      }
    }

    const rows = Array.isArray(fixturesRes.data) ? fixturesRes.data : [];
    const due = rows.flatMap((row) => {
      let seasonYear = null;
      try {
        seasonYear = new Date(row.match_date + "T00:00:00Z").getUTCFullYear();
      } catch {
        seasonYear = null;
      }
      if (seasonYear !== currentSeason) return [];

      let kickoffUtc = null;
      try {
        kickoffUtc = row?.venue?.timezone
          ? fixtureKickoffUtc(row.match_date, String(row.kickoff_local).slice(0, 5), row.venue.timezone)
          : null;
      } catch {
        kickoffUtc = null;
      }
      if (!kickoffUtc || kickoffUtc.getTime() > now.getTime()) return [];

      return [{ row, seasonYear, kickoffUtc }];
    });
    // R11: order/group/key by scoringRound (equal to round for regular
    // fixtures; the scoring round for Origin). Origin I (scoringRound 13)
    // sorts inside the round-13 group. Items with a null scoringRound are kept
    // and grouped separately by the caller. An ABSENT scoring_round maps to the
    // feed round (R9 absent/null rule).
    const effectiveRound = (r) =>
      Object.prototype.hasOwnProperty.call(r, "scoring_round") ? r.scoring_round : r.round;
    due.sort((a, b) => {
      const ar = effectiveRound(a.row);
      const br = effectiveRound(b.row);
      if (ar !== br) {
        if (ar === null) return 1;
        if (br === null) return -1;
        return br - ar;
      }
      return a.kickoffUtc.getTime() - b.kickoffUtc.getTime();
    });

    return due.map(({ row, seasonYear }) => {
      const confirmationRow = Array.isArray(row?.confirmation)
        ? row.confirmation[0]
        : row?.confirmation;
      const source = confirmationRow?.source ?? null;
      const confirmedAt = confirmationRow?.confirmed_at ?? null;
      // R11: release state is keyed by scoringRound (server truth). An Origin
      // fixture shares its scoringRound's releasedAt. Same absent/null rule as
      // R9: an ABSENT scoring_round maps to the feed round; an explicit null
      // stays null (never `?? row.round`).
      const scoringRound = Object.prototype.hasOwnProperty.call(row, "scoring_round")
        ? row.scoring_round
        : row.round;
      const releasedAt = scoringRound === null
        ? null
        : (releaseByRound.get(`${seasonYear}:${scoringRound}`) ?? null);
      // R17: `originComplete` is the R5 completeness predicate for this Origin
      // fixture — a parent exists, the side ids are two distinct non-null
      // representative ids, and each side holds 13–19 distinct child identities.
      // Read from the `match_confirmation_players` embedded INSIDE the
      // confirmation row (its fixture_id references match_confirmations, not
      // fixtures, so PostgREST refuses a direct fixtures embed with PGRST200);
      // club fixtures never carry `originComplete`.
      const lineupRows = Array.isArray(confirmationRow?.players) ? confirmationRow.players : [];
      const homeIds = new Set(lineupRows.filter(p => p?.side === "home").map(p => p?.player_id).filter(Boolean));
      const awayIds = new Set(lineupRows.filter(p => p?.side === "away").map(p => p?.player_id).filter(Boolean));
      const originComplete = Boolean(row?.is_origin) &&
        Boolean(confirmationRow) &&
        row?.home_team_id != null && row?.away_team_id != null && row.home_team_id !== row.away_team_id &&
        homeIds.size >= 13 && homeIds.size <= 19 && awayIds.size >= 13 && awayIds.size <= 19;
      return {
        id: row.id,
        round: row.round,
        scoringRound: scoringRound,
        isOrigin: Boolean(row.is_origin),
        originGame: row.origin_game ?? null,
        matchDate: row.match_date,
        kickoffLocal: row.kickoff_local,
        homeRef: row.home_ref,
        awayRef: row.away_ref,
        homeTeamId: row.home_team_id ?? null,
        awayTeamId: row.away_team_id ?? null,
        externalRef: row.external_ref,
        status: row.status,
        venueName: row?.venue?.name ?? null,
        confirmed: Boolean(confirmationRow),
        confirmation: source
          ? { source, confirmedAt }
          : null,
        releasedAt: releasedAt,
        ...(row?.is_origin ? { originComplete: originComplete } : {}),
      };
    });
  } catch (err) {
    console.warn("[adminApi] could not load confirmation queue:", err?.message || err);
    return null;
  }
}

/**
 * Invoke the match-prefill Edge Function for a fixture. Returns the feed v3
 * pre-fill payload on success, or a mapped error code on failure.
 */
export async function prefillMatch(fixtureId) {
  let response;
  try {
    response = await supabase.functions.invoke("match-prefill", {
      body: { fixture_id: fixtureId },
    });
  } catch {
    return { ok: false, code: "network_error" };
  }
  if (response.error) return errorResult(response.error, PREFILL_CODES);
  const d = response.data;
  return {
    ok: true,
    provider: d?.provider ?? null,
    score: d?.score,
    squads: d?.squads,
    tryScorers: d?.tryScorers,
    milestones: d?.milestones,
    source: d?.source,
  };
}

/**
 * PURE: turn a prefillMatch success payload into the screen's draft state —
 * home/away selection rows (one per named feed squad player, carrying the
 * feed's resolution + candidates), a milestone list, and the completeness flag.
 * tries, goals and field goals are pulled from tryScorers by externalRef
 * (preferred) or name — the feed emits a tryScorer row for every player with
 * tries OR goals OR field goals > 0 (a goal-only kicker carries tries:0), so a
 * goal-only kicker's goals land on their squad row exactly as tries do. The
 * screen ticks resolved rows (playerId set) and shows the rest in a
 * "From the feed, not matched" panel.
 */
export function prefillToDraft(prefill) {
  const homeSquad = prefill?.squads?.home ?? [];
  const awaySquad = prefill?.squads?.away ?? [];

  const triesByRef = new Map();
  const triesByName = new Map();
  const goalsByRef = new Map();
  const goalsByName = new Map();
  const fieldGoalsByRef = new Map();
  const fieldGoalsByName = new Map();
  const indexScorers = (scorers) => {
    (scorers ?? []).forEach((s) => {
      const tries = Number(s?.tries) || 0;
      const goals = Number(s?.goals) || 0;
      const fieldGoals = Number(s?.fieldGoals) || 0;
      const ref = s?.externalRef;
      const name = s?.name;
      if (ref) {
        triesByRef.set(ref, (triesByRef.get(ref) || 0) + tries);
        goalsByRef.set(ref, (goalsByRef.get(ref) || 0) + goals);
        fieldGoalsByRef.set(ref, (fieldGoalsByRef.get(ref) || 0) + fieldGoals);
      }
      if (name) {
        triesByName.set(name, (triesByName.get(name) || 0) + tries);
        goalsByName.set(name, (goalsByName.get(name) || 0) + goals);
        fieldGoalsByName.set(name, (fieldGoalsByName.get(name) || 0) + fieldGoals);
      }
    });
  };
  indexScorers(prefill?.tryScorers?.home);
  indexScorers(prefill?.tryScorers?.away);

  const statFor = (map, p) => {
    const ref = p?.externalRef;
    if (ref && map.has(ref)) return map.get(ref);
    if (p?.name) {
      const byName = map === triesByRef ? triesByName : map === goalsByRef ? goalsByName : fieldGoalsByName;
      if (byName.has(p.name)) return byName.get(p.name);
    }
    return 0;
  };

  const toSel = (p) => {
    const externalRef = p?.externalRef ?? null;
    return {
      playerId: p?.playerId ?? null,
      name: p?.name ?? "",
      externalRef,
      dateOfBirth: p?.dateOfBirth ?? null,
      // F2: carry the matched identity's dated club label through the draft so
      // pre-fill-ticked players show their club label immediately (the backend
      // lane adds `clubName` to matched rows in the edge response).
      clubName: p?.clubName ?? null,
      resolution: p?.resolution ?? "unresolved",
      ...(p?.resolution === "other_team" ? {
        currentTeamId: p.currentTeamId ?? null,
        currentTeamName: p.currentTeamName ?? null,
      } : {}),
      candidates: Array.isArray(p?.candidates) ? p.candidates : [],
      tries: statFor(triesByRef, p),
      goals: statFor(goalsByRef, p),
      fieldGoals: statFor(fieldGoalsByRef, p),
    };
  };

  return {
    home: homeSquad.map(toSel),
    away: awaySquad.map(toSel),
    milestones: (prefill?.milestones ?? []).map((m) => ({
      playerId: m?.playerId ?? null,
      playerName: m?.playerName ?? "",
      code: m?.code ?? "",
    })),
    complete: Boolean(prefill?.score?.complete),
  };
}

/**
 * PURE: turn the screen's draft textareas + tries/milestones maps into the
 * legacy match_confirmations name-mirror shape. Retained for the audit mirror
 * path; the live screen saves through save_match_confirmation (player-id keyed)
 * — this helper is kept exported so the App.jsx import surface stays stable.
 * Trims lines, drops blanks and duplicates, drops tries ≤ 0 or for players not
 * in either list, drops blank milestones. `source` is normalised to feed|manual.
 */
export function draftToConfirmation({ fixtureId, homeText, awayText, tries, milestones, source }) {
  const dedupe = (text) => {
    const seen = new Set();
    const out = [];
    (String(text || "").split("\n")).forEach((line) => {
      const name = line.trim();
      if (!name || seen.has(name)) return;
      seen.add(name);
      out.push(name);
    });
    return out;
  };
  const home_players = dedupe(homeText);
  const away_players = dedupe(awayText);
  const allowed = new Set([...home_players, ...away_players]);

  const try_scorers = {};
  Object.entries(tries || {}).forEach(([name, count]) => {
    const n = Number(count);
    if (!allowed.has(name) || !Number.isFinite(n) || n <= 0) return;
    try_scorers[name] = n;
  });

  const milestoneRows = [];
  Object.entries(milestones || {}).forEach(([player, note]) => {
    const noteTrim = String(note || "").trim();
    if (!noteTrim) return;
    if (!allowed.has(player)) return;
    milestoneRows.push({ player, note: noteTrim });
  });

  return {
    fixture_id: fixtureId,
    home_players,
    away_players,
    try_scorers,
    milestones: milestoneRows,
    source: source === "feed" ? "feed" : "manual",
  };
}

/**
 * Save a player-id-keyed confirmation through the save_match_confirmation RPC.
 * `players` is [{ player_id, side, tries, goals, field_goals }];
 * `milestones` is [{ player_id, milestone_code }]. A 42501 / RLS rejection maps
 * to `forbidden`; a message starting with a stable token maps to that token;
 * anything else is `server_error`.
 */
export async function saveMatchConfirmation({ fixtureId, players, milestones, source }) {
  try {
    const { error } = await supabase.rpc("save_match_confirmation", {
      p_fixture_id: fixtureId,
      p_players: (players ?? []).map((p) => ({
        player_id: p.player_id,
        side: p.side,
        tries: Number(p.tries) || 0,
        goals: Number(p.goals) || 0,
        field_goals: Number(p.field_goals) || 0,
      })),
      p_milestones: (milestones ?? []).map((m) => ({
        player_id: m.player_id,
        milestone_code: m.milestone_code,
      })),
      p_source: source === "feed" ? "feed" : "manual",
    });
    if (error) {
      console.warn("[adminApi] saveMatchConfirmation failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true };
  } catch (err) {
    console.warn("[adminApi] saveMatchConfirmation failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * R9/R16 — search every identity (including inactive/unassigned) for the Origin
 * line-up editor through `admin_search_players(p_query, p_on, p_limit)`. `onDate`
 * is the fixture's match_date so the club label reflects who was squadded that
 * day. Returns `[{ id, display_name, club_name, has_rlp_ref }]` or `[]` on any
 * error (the editor shows the error inline; a failed search never blocks the
 * line-up).
 */
export async function searchOriginPlayers(query, onDate) {
  try {
    const { data, error } = await supabase.rpc("admin_search_players", {
      p_query: query,
      p_on: onDate,
    });
    if (error) {
      console.warn("[adminApi] admin_search_players failed:", error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn("[adminApi] admin_search_players failed:", err?.message || err);
    return [];
  }
}

/**
 * R9/R16 — reopen a confirmed Origin line-up through
 * `admin_get_confirmation(p_fixture_id)`. Returns the jsonb
 * `{ fixture_id, source, confirmed_by, confirmed_at, players[] }` or SQL null
 * when no parent exists. `players[]` carries `player_id, display_name,
 * club_name, side, tries, goals, field_goals` (dated club label), ordered home
 * before away, then display name, then UUID.
 */
export async function fetchOriginConfirmation(fixtureId) {
  try {
    const { data, error } = await supabase.rpc("admin_get_confirmation", {
      p_fixture_id: fixtureId,
    });
    if (error) {
      console.warn("[adminApi] admin_get_confirmation failed:", error.message);
      return null;
    }
    return data ?? null;
  } catch (err) {
    console.warn("[adminApi] admin_get_confirmation failed:", err?.message || err);
    return null;
  }
}

/**
 * Invoke the release-round Edge Function. A 422 `matches_unconfirmed` carries
 * the named fixtures; other failure codes map as documented.
 */
export async function releaseRound({ season, round }) {
  let response;
  try {
    response = await supabase.functions.invoke("release-round", {
      body: { season, round },
    });
  } catch {
    return { ok: false, code: "network_error" };
  }
  if (response.error) return errorResult(response.error, RELEASE_CODES);
  return { ok: true, released: response.data.released };
}

/**
 * Fetch a team's active players covering a given date (defaults to today on
 * the server) through the admin-only `admin_team_players` RPC (the DB task
 * makes `date_of_birth` admin-only, so the client must not read the `players`
 * table directly). `onDate` is passed as `p_on` — Weekly Confirmation passes
 * the fixture's match_date so the roster matches who was actually squadded
 * that day (R31). Returns [{ id, name, dateOfBirth, externalRef, startsOn,
 * endsOn, kind }] or null on error.
 */
export async function fetchTeamPlayers(teamId, onDate = null) {
  try {
    const { data, error } = await supabase.rpc("admin_team_players", {
      p_team_id: teamId,
      p_on: onDate,
    });
    if (error) {
      console.warn("[adminApi] could not load team players:", error.message);
      return null;
    }
    return (Array.isArray(data) ? data : []).map((row) => ({
      id: row.id,
      name: row.name,
      dateOfBirth: row.date_of_birth ?? null,
      externalRef: row.external_ref ?? null,
      startsOn: row.starts_on ?? null,
      endsOn: row.ends_on ?? null,
      kind: row.kind ?? null,
    }));
  } catch (err) {
    console.warn("[adminApi] could not load team players:", err?.message || err);
    return null;
  }
}

/**
 * Add a player through the admin_add_player RPC (enforces the namesake rule and
 * external_ref uniqueness). Returns { ok, id } on success or { ok:false, code }
 * with a stable token (forbidden, invalid_name, unknown_team,
 * namesake_requires_date_of_birth, namesake_missing_date_of_birth,
 * duplicate_player, external_ref_taken).
 */
export async function addPlayer({ teamId, name, dateOfBirth, externalRef, startsOn }) {
  try {
    const { data, error } = await supabase.rpc("admin_add_player", {
      p_team_id: teamId,
      p_name: name,
      p_date_of_birth: dateOfBirth ?? null,
      p_external_ref: externalRef ?? null,
      p_starts_on: startsOn ?? null,
    });
    if (error) {
      console.warn("[adminApi] addPlayer failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true, id: data };
  } catch (err) {
    console.warn("[adminApi] addPlayer failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * Stamp a player's date of birth + external_ref through the
 * admin_set_player_identity RPC. Returns { ok:true } or { ok:false, code }.
 */
export async function stampPlayerIdentity({ playerId, dateOfBirth, externalRef }) {
  try {
    const { error } = await supabase.rpc("admin_set_player_identity", {
      p_player_id: playerId,
      p_date_of_birth: dateOfBirth ?? null,
      p_external_ref: externalRef ?? null,
    });
    if (error) {
      console.warn("[adminApi] stampPlayerIdentity failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true };
  } catch (err) {
    console.warn("[adminApi] stampPlayerIdentity failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * Fetch the milestone badge codes for the per-player milestone selects. Returns
 * [{ id, name, description }] or null on error.
 */
export async function fetchMilestoneCodes() {
  try {
    const res = await supabase
      .from("badge_definitions")
      .select("id,name,description")
      .eq("category", "milestones")
      .order("points");
    if (res.error) {
      console.warn("[adminApi] could not load milestone codes:", res.error.message);
      return null;
    }
    return (res.data ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      description: row.description,
    }));
  } catch (err) {
    console.warn("[adminApi] could not load milestone codes:", err?.message || err);
    return null;
  }
}

/**
 * Fetch unacknowledged admin notifications for the banner. Returns
 * [{ id, kind, severity, message, payload, fixtureId, round, seasonYear,
 *    createdAt }] or [] on error (a missing banner is non-fatal).
 */
export async function fetchAdminNotifications() {
  try {
    const res = await supabase
      .from("admin_notifications")
      .select("id,kind,severity,message,payload,fixture_id,round,season_year,created_at")
      .is("acknowledged_at", null)
      .order("created_at", { ascending: false });
    if (res.error) {
      console.warn("[adminApi] could not load admin notifications:", res.error.message);
      return [];
    }
    return (res.data ?? []).map((row) => ({
      id: row.id,
      kind: row.kind,
      severity: row.severity,
      message: row.message,
      payload: row.payload ?? {},
      fixtureId: row.fixture_id ?? null,
      round: row.round ?? null,
      seasonYear: row.season_year ?? null,
      createdAt: row.created_at ?? null,
    }));
  } catch (err) {
    console.warn("[adminApi] could not load admin notifications:", err?.message || err);
    return [];
  }
}

/**
 * Acknowledge (dismiss) an admin notification through the RPC. Idempotent.
 */
export async function acknowledgeNotification(id) {
  try {
    const { error } = await supabase.rpc("acknowledge_admin_notification", {
      p_id: id,
    });
    if (error) {
      console.warn("[adminApi] acknowledgeNotification failed:", error.message);
      return rpcErrorResult(error);
    }
    return { ok: true };
  } catch (err) {
    console.warn("[adminApi] acknowledgeNotification failed:", err?.message || err);
    return { ok: false, code: "server_error" };
  }
}

/**
 * Invoke the sync-fixtures Edge Function for a season+round. Returns
 * { ok, matched, unmatched, updatedScores } on success or { ok:false, code }
 * (unknown_alias, no_data, provider_mismatch, feed_unavailable,
 * budget_exhausted, forbidden, unauthenticated, invalid_input, write_failed,
 * server_error, network_error).
 */
export async function syncRound({ season, round }) {
  let response;
  try {
    response = await supabase.functions.invoke("sync-fixtures", {
      body: { season, round },
    });
  } catch {
    return { ok: false, code: "network_error" };
  }
  if (response.error) return errorResult(response.error, SYNC_CODES);
    const d = response.data ?? {};
  return {
    ok: true,
    matched: d.matched,
    unmatched: d.unmatched,
    updatedScores: d.updated_scores,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Round 11 — admin replay-clock API (R7/R8/R14). Every action re-syncs the
// clock on success (AdminReplayScreen does this); these wrappers only map the
// RPC result/error, they never touch appClock state themselves.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Call admin_app_clock(action, at). `action` is one of next_game, next_round,
 * end_round, set, real. `at` (ISO string or Date) is required for 'set'.
 * Returns { ok:true, clock: get_app_clock() payload } or { ok:false, code }
 * with a stable token (forbidden, replay_off, no_next_fixture, invalid_at,
 * invalid_action, clock_backwards, server_error, network_error).
 */
export async function adminAppClock(action, at = null) {
  try {
    const { data, error } = await supabase.rpc("admin_app_clock", {
      p_action: action,
      p_at: at instanceof Date ? at.toISOString() : at,
    });
    if (error) {
      console.warn("[adminApi] adminAppClock failed:", error.message);
      return /fetch|network/i.test(error.message || "") ? { ok: false, code: "network_error" } : rpcErrorResult(error);
    }
    return { ok: true, clock: data };
  } catch (err) {
    console.warn("[adminApi] adminAppClock failed:", err?.message || err);
    return { ok: false, code: "network_error" };
  }
}

/**
 * Fetch every member's tester stand-in flag through admin_location_standins().
 * Returns [{ userId, displayName, isAdmin, locationStandin }] or null on error.
 */
export async function adminLocationStandins() {
  try {
    const { data, error } = await supabase.rpc("admin_location_standins");
    if (error) {
      console.warn("[adminApi] adminLocationStandins failed:", error.message);
      return null;
    }
    return (Array.isArray(data) ? data : []).map((row) => ({
      userId: row.user_id,
      displayName: row.display_name,
      isAdmin: Boolean(row.is_admin),
      locationStandin: Boolean(row.location_standin),
    }));
  } catch (err) {
    console.warn("[adminApi] adminLocationStandins failed:", err?.message || err);
    return null;
  }
}

/**
 * Set a member's tester stand-in flag through admin_set_location_standin.
 * Returns { ok:true, userId, locationStandin } or { ok:false, code }
 * (forbidden, unknown_member, server_error, network_error).
 */
export async function adminSetLocationStandin(userId, on) {
  try {
    const { data, error } = await supabase.rpc("admin_set_location_standin", {
      p_user_id: userId,
      p_on: Boolean(on),
    });
    if (error) {
      console.warn("[adminApi] adminSetLocationStandin failed:", error.message);
      return /fetch|network/i.test(error.message || "") ? { ok: false, code: "network_error" } : rpcErrorResult(error);
    }
    return { ok: true, userId: data?.user_id ?? userId, locationStandin: Boolean(data?.location_standin) };
  } catch (err) {
    console.warn("[adminApi] adminSetLocationStandin failed:", err?.message || err);
    return { ok: false, code: "network_error" };
  }
}
